import { of, from, concat, forkJoin, interval, EMPTY } from "rxjs";
import { takeUntil, switchMap, startWith } from "rxjs/operators";
import { api, apiConfig } from "../api/api";
import uuid from "uuid/v1";
import { mergeMap, map, catchError, withLatestFrom } from "rxjs/operators";
import { combineEpics, ofType } from "redux-observable";
import * as R from "ramda";
import * as Promise from "bluebird";
import {
  types,
  setPlugins,
  subscribePluginDone,
  getPluginSubscriptionsDone,
  getPluginSubscriptions as getPluginSubscriptionsAction,
  setJobServices,
  cancelPluginSubscriptionDone,
  setSalesPersons,
  woocommerceCreateCustomer,
  woocommerceCreateCustomerDone,
  woocommerceUpdateCustomer,
  woocommerceUpdateCustomerDone,
  woocommerceAddToOrder,
  woocommerceAddToOrderDone,
  woocommerceCreateOrderDone,
  woocommerceCheckUserExistsDone,
  woocommerceCheckOrderExistsDone,
  woocommerceCheckOrderExists,
  setLocations,
  woocommerceCreateOrder,
  woocommerceGetProductIdFailure,
  woocommerceGetProductIdDone,
  woocommerceGetProductId,
  woocommerceDeleteOrderDone,
  woocommerceRemoveFromOrderDone,
  woocommerceRemoveFromOrder,
  woocommerceCreateCustomerFailure,
  woocommerceCheckSKUExistsDone,
  woocommerceAddToOrderFromSKU,
  woocommerceAddToOrderFromSKUDone,
  woocommerceRemoveFromOrderBySKU,
  woocommerceRemoveFromOrderBySKUDone,
  woocommerceReplaceOrder,
  woocommerceReplaceOrderDone,
  woocommerceCreateProductDone,
  woocommerceCreateProductFailure,
  addJobService,
  provisionSpectrePOSDone,
  provisionSpectrePOSError,
  setProvisionStatus,
  stopProvisionPolling,
  setPluginSettings,
  setSpectreposSiteStatus,
  getSpectreposSiteStatus,
  getProvisionStatus as getProvisionStatusAction,
  woocommerceMatchesFound,
  writeSpectrePosLink,
  writeSpectrePosLinkDone,
  checkSpectrePosLink,
  woocommerceCheckBallInOpenOrderDone,
} from "./plugin.redux";
import hbsShopEpics, {
  isHbsShop,
  posSendEnabled,
  whenPosSendOff,
} from "./plugin.hbs.epic";
import { datasources } from "../db/datasources";
import Notify from "../ui/Notify";
import i18n from "../i18n";
import OAuth from "oauth-1.0a";
import CryptoJS from "crypto-js";

Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const normalizePhone = (s) => (s || "").replace(/\D+/g, "");

const parseExtraValues = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const getPosLinkFromSpectre = (spectreCustomer) => {
  if (!spectreCustomer) return null;
  const ev = parseExtraValues(spectreCustomer.extra_values);
  if (!ev || !ev.pos) return null;
  const customerId = parseInt(ev.pos.customer_id, 10);
  if (!customerId) return null;
  return { customer_id: customerId, blog_id: ev.pos.blog_id || null };
};

const filterWcCandidates = (results, { first_name, last_name, email, telephone }) => {
  if (!Array.isArray(results)) return [];
  const emailLc = (email || "").toLowerCase().trim();
  const phone = normalizePhone(telephone);
  const fnLc = (first_name || "").toLowerCase().trim();
  const lnLc = (last_name || "").toLowerCase().trim();

  return results.filter((c) => {
    const cEmail = (c.email || "").toLowerCase().trim();
    if (emailLc && cEmail && cEmail === emailLc) return true;

    const cFn = (c.first_name || c.billing?.first_name || "")
      .toLowerCase()
      .trim();
    const cLn = (c.last_name || c.billing?.last_name || "")
      .toLowerCase()
      .trim();
    const cPhone = normalizePhone(c.billing?.phone || "");

    if (lnLc && cLn && cLn === lnLc) {
      if (fnLc && cFn && cFn === fnLc) return true;
      if (phone && cPhone && cPhone === phone) return true;
    }

    return false;
  });
};

const signWoocommerceUrl = (baseUrl, method, consumerKey, consumerSecret) => {
  const oauth = new OAuth({
    consumer: { key: consumerKey, secret: consumerSecret },
    signature_method: "HMAC-SHA256",
    hash_function(baseString, key) {
      return CryptoJS.HmacSHA256(baseString, key).toString(CryptoJS.enc.Base64);
    },
  });
  const oauthParams = oauth.authorize({ url: baseUrl, method });
  const oauthQuery = new URLSearchParams(oauthParams).toString();
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}${oauthQuery}`;
};

// Resolves to true if the WC customer id still exists on this site. A stale
// cached pos.customer_id (test site re-provisioned, wrong env/blog, or a
// deleted customer) otherwise makes every order op fail with
// woocommerce_rest_invalid_customer_id. Returns an Observable<boolean>.
const wcCustomerExists = (apiBase, consumerKey, consumerSecret, customerId) =>
  from(
    fetch(
      signWoocommerceUrl(
        `${apiBase}/wp-json/wc/v3/customers/${customerId}`,
        "GET",
        consumerKey,
        consumerSecret,
      ),
      { method: "GET", headers: { "Content-Type": "application/json" } },
    )
      .then((r) => r.ok)
      .catch(() => false),
  );

// Build a WC-shaped customer payload from a v3 spectreCustomer for the
// background auto-create flow. WC core requires a top-level `email`, so when
// the bowler has none we synthesize a placeholder that's clearly identifiable
// and unique per v3 customer id. Username is set when we have a phone (matches
// the manual UpdateProfile flow) and otherwise synthesized.
// Synthesize an email when a create-customer payload arrives with email
// blank. WC core rejects POST /customers with empty/invalid `billing.email`
// even when the top-level email is set, so we mirror the synthesized value
// to both spots. Called from the create epic chokepoint so every path
// (auto-create after 0 matches, "Crear nuevo" picker, no-search shortcut)
// gets the same treatment.
// WooCommerce / WP `validate_username` runs `sanitize_user($u, true)` and
// rejects the value when the sanitized result differs from the original. A raw
// phone like "(555) 345-6789" keeps its parens/spaces and fails with
// registration-error-invalid-username. Pre-strip to a WP-safe charset so what we
// send already equals what sanitize_user would produce.
const sanitizeWcUsername = (value, fallback) => {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "");
  return cleaned || fallback;
};

const ensureCustomerCreateEmail = (data, spectreCustomer) => {
  if (!data) return data;
  const topEmail = data.email && String(data.email).trim();
  const billingEmail =
    data.billing?.email && String(data.billing.email).trim();

  const seedFromId = String(spectreCustomer?.id || data.username || "")
    .replace(/-/g, "")
    .slice(0, 12)
    .toLowerCase();
  // Always normalize the username — WC rejects raw phones/parens even when the
  // email path is otherwise valid (registration-error-invalid-username).
  const safeUsername = sanitizeWcUsername(
    data.username,
    `spectre-${seedFromId || "user"}`,
  );

  if (topEmail && billingEmail)
    return data.username === safeUsername
      ? data
      : { ...data, username: safeUsername };

  const seed = String(spectreCustomer?.id || data.username || "")
    .replace(/-/g, "")
    .slice(0, 12)
    .toLowerCase();
  const idShort = seed || Math.random().toString(36).slice(2, 14);
  const synthesized = topEmail || `spectre-${idShort}@spectrepos.local`;
  return {
    ...data,
    email: synthesized,
    username: sanitizeWcUsername(safeUsername, `spectre-${idShort}`),
    billing: { ...(data.billing || {}), email: synthesized },
  };
};

const buildAutoCreateCustomerData = (sc) => {
  const idShort = String(sc?.id || "")
    .replace(/-/g, "")
    .slice(0, 12)
    .toLowerCase();
  const realEmail = sc?.email && String(sc.email).trim();
  const email = realEmail
    ? String(sc.email).trim()
    : `spectre-${idShort}@spectrepos.local`;
  const username = sanitizeWcUsername(sc?.telephone, `spectre-${idShort}`);

  return {
    first_name: sc?.firstname || "",
    last_name: sc?.lastname || "",
    email: email,
    username: username,
    billing: {
      first_name: sc?.firstname || "",
      last_name: sc?.lastname || "",
      address_1: sc?.address || "",
      address_2: "",
      city: sc?.city || "",
      state: sc?.state || "",
      postcode: sc?.postal_code || "",
      country: sc?.country || "",
      // WC core validates billing.email and rejects empty/invalid even
      // when the top-level email is set. Mirror the same value so the
      // payload is internally consistent and passes validation.
      email: email,
      phone: sc?.telephone || "",
    },
    shipping: {
      first_name: sc?.firstname || "",
      last_name: sc?.lastname || "",
      company: "",
      address_1: sc?.address || "",
      address_2: "",
      city: sc?.city || "",
      state: sc?.state || "",
      postcode: sc?.postal_code || "",
      country: sc?.country || "",
    },
  };
};

const buildPostMatchActions = (wcCustomerId, payload) => {
  const {
    first_name,
    last_name,
    email,
    sku = "",
    ball_name = "",
    manufacturer = "",
    old_ball_name = "",
    old_manufacturer = "",
    orderData,
    customerData,
    nextAction,
    spectreCustomer,
    insertSkus,
    replaceMode,
    existingBallLineItemIds,
  } = payload;

  const baseDone = of(
    woocommerceCheckUserExistsDone({ customerId: wcCustomerId }),
  );

  const linkWrite =
    spectreCustomer && spectreCustomer.id
      ? of(
          writeSpectrePosLink({
            wcCustomerId,
            spectreCustomerId: spectreCustomer.id,
            spectreExtraValues: spectreCustomer.extra_values,
          }),
        )
      : EMPTY;

  if (nextAction === "update") {
    return concat(
      baseDone,
      linkWrite,
      of(
        woocommerceUpdateCustomer({ customerId: wcCustomerId, customerData }),
      ),
    );
  }
  if (
    nextAction === "order" ||
    nextAction === "remove order" ||
    nextAction === "replace order"
  ) {
    return concat(
      baseDone,
      linkWrite,
      of(
        woocommerceGetProductId({
          first_name,
          last_name,
          email,
          ball_name,
          manufacturer,
          old_ball_name,
          old_manufacturer,
          orderData: { ...orderData, customer_id: wcCustomerId },
          customerData,
          nextAction,
        }),
      ),
    );
  }
  if (nextAction === "SKU add") {
    return concat(
      baseDone,
      linkWrite,
      of(
        woocommerceAddToOrderFromSKU({
          first_name,
          last_name,
          email,
          sku,
          ball_name,
          manufacturer,
          orderData: { ...orderData, customer_id: wcCustomerId },
          customerData,
          nextAction,
          insertSkus: insertSkus || [],
          replaceMode: !!replaceMode,
          existingBallLineItemIds: existingBallLineItemIds || [],
        }),
      ),
    );
  }
  if (nextAction === "SKU remove") {
    return concat(
      baseDone,
      linkWrite,
      of(
        woocommerceRemoveFromOrderBySKU({
          first_name,
          last_name,
          email,
          sku,
          ball_name,
          manufacturer,
          orderData: { ...orderData, customer_id: wcCustomerId },
          customerData,
          nextAction,
        }),
      ),
    );
  }
  return concat(baseDone, linkWrite);
};

const getPlugins = ({ isCAD, isSandbox, isPaddle }) => {
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax(!isPaddle ? "getPlugins" : "getPluginsPaddle", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { isCAD, isSandbox },
    })
      .then(success)
      .catch(fail);
  });
};

const subscribePlugin = ({
  stripeCustomerId,
  address_id,
  currency_code,
  priceId,
  pluginType,
  isSandbox,
  isPaddleSubscription,
}) => {
  const { ajaxSession } = api;

  return new Promise((success, fail) => {
    const subscribePluginUrl = !isPaddleSubscription
      ? "subscribeUserPlugin"
      : "subscribeUserPluginPaddle";

    ajaxSession(subscribePluginUrl, {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: {
        stripeCustomerId,
        priceId,
        pluginType,
        isSandbox,
        address_id,
        currency_code,
      },
    })
      .then(success)
      .catch(fail);
  });
};

const provisionSpectrePOSSite = ({ userId, isSandbox }) => {
  const { ajaxSession } = api;

  return new Promise((success, fail) => {
    ajaxSession("provisionSpectrePOS", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: { userId, isSandbox },
    })
      .then(success)
      .catch(fail);
  });
};

const fetchProvisionStatus = ({ userId, isSandbox }) => {
  const { ajaxSession } = api;

  return new Promise((success, fail) => {
    ajaxSession("getProvisionSpectrePOSStatus", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { userId, isSandbox },
    })
      .then(success)
      .catch(fail);
  });
};

const fetchSpectreposSiteStatus = ({ userId, isSandbox }) => {
  const { ajaxSession } = api;

  return new Promise((success, fail) => {
    ajaxSession("getSpectreposSiteStatus", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      params: { userId, isSandbox },
    })
      .then(success)
      .catch(fail);
  });
};

const cancelSubscription = ({ subscriptionId, isSandbox }) => {
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax("cancelSubscriptionPaddle", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: { subscriptionId, isSandbox },
    })
      .then((result) => {
        if (window) {
          window.location.reload();
        }
        return success(result);
      })
      .catch(fail);
  });
};

const getPluginSubscriptions = ({ stripeCustomerId, isSandbox, isPaddle }) => {
  const { ajax } = api;

  return new Promise((success, fail) => {
    ajax(
      !isPaddle ? "getPluginSubscriptions" : "getPluginSubscriptionsPaddle",
      {
        noWorker: true,
        url: apiConfig.appUrl,
        type: "GET",
        params: { stripeCustomerId, isSandbox },
      },
    )
      .then(success)
      .catch(fail);
  });
};

const getPluginsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getPlugins),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      from(
        getPlugins({
          isCAD: state.userSession.isCAD,
          isSandbox: state.userSession.isSandbox,
          isPaddle: state.userSession.isPaddleSubscription,
        }),
      ).pipe(
        map((plugins) => setPlugins({ plugins })),
        catchError((error) => {
          console.warn(error);
          return of(setPlugins({ plugins: [] }));
        }),
      ),
    ),
  );

const subscribePluginEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.subscribePlugin),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { priceId, pluginType },
        },
        state,
      ]) =>
        from(
          subscribePlugin({
            stripeCustomerId:
              state.userSession.subscriptionInfo.customer ||
              state.userSession.subscriptionInfo.customer_id,
            address_id: state.userSession.subscriptionInfo.address_id,
            currency_code: state.userSession.subscriptionInfo.currency_code,
            priceId,
            pluginType,
            isSandbox: state.userSession.isSandbox,
            isPaddleSubscription: state.userSession.isPaddleSubscription,
          }),
        ).pipe(
          map((plugins) => getPluginSubscriptionsAction()),
          catchError((error) => {
            console.warn(error);
            return of(subscribePluginDone());
          }),
        ),
    ),
  );

const getPluginSubscriptionsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getPluginSubscriptions),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      from(
        getPluginSubscriptions({
          stripeCustomerId:
            state.userSession.subscriptionInfo.customer ||
            state.userSession.subscriptionInfo.customer_id,
          isSandbox: state.userSession.isSandbox,
          isPaddle: state.userSession.isPaddleSubscription,
        }),
      ).pipe(
        map((pluginSubscriptions) => {
          let values = pluginSubscriptions;
          if (typeof pluginSubscriptions === "object") {
            values = R.keys(pluginSubscriptions).map(
              (key) => pluginSubscriptions[key],
            );
          }

          return getPluginSubscriptionsDone({ pluginSubscriptions: values });
        }),
        catchError((error) => {
          console.warn("getPluginSubscriptions Error", error);
          return of(getPluginSubscriptionsDone({ pluginSubscriptions: [] }));
        }),
      ),
    ),
  );

const addJobServiceEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.addJobService),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { jobService },
        },
        state,
      ]) => {
        const proccessJobService = async () => {
          try {
            const { jobServiceDS } = datasources;
            const id = uuid();
            const newJobService = await jobServiceDS.create({
              ...jobService,
              localId: id,
            });

            return {
              jobService: newJobService,
            };
          } catch (e) {
            console.error(e);
            throw e;
          }
        };

        return from(proccessJobService()).pipe(
          map(({ jobService }) => {
            return setJobServices({
              jobServices: [...state.plugin.jobServices, jobService],
            });
          }),
          catchError((error) => of(setJobServices({ jobServices: [] }))),
        );
      },
    ),
  );

const addSalesPersonEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.addSalesPerson),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { salesPerson },
        },
        state,
      ]) => {
        const createSalesPerson = async () => {
          try {
            const { salesPersonDS } = datasources;
            const id = uuid();
            const newSalesPerson = await salesPersonDS.create({
              ...salesPerson,
              localId: id,
            });
            return newSalesPerson;
          } catch (error) {
            console.error("Error adding salesperson:", error);
            throw error;
          }
        };

        return from(createSalesPerson()).pipe(
          map((newSalesPerson) =>
            setSalesPersons({
              salesPersons: [...state.plugin.salesPersons, newSalesPerson],
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(
              setSalesPersons({ salesPersons: state.plugin.salesPersons }),
            );
          }),
        );
      },
    ),
  );

const addLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.addLocation),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { location },
        },
        state,
      ]) => {
        const createLocation = async () => {
          try {
            const { jobLocationDS } = datasources;
            const newLocation = await jobLocationDS.create({
              ...location,
              user_id: state.userSession.proshopId, // Associate with the current user
            });
            return newLocation;
          } catch (error) {
            console.error("Error adding salesperson:", error);
            throw error;
          }
        };

        return from(createLocation()).pipe(
          map((newLocation) =>
            setLocations({
              locations: [...state.plugin.locations, newLocation],
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(setLocations({ locations: state.plugin.locations }));
          }),
        );
      },
    ),
  );

const updateJobServicesEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.updateJobServices),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { jobServices },
        },
        state,
      ]) => {
        const modifyJobServices = async () => {
          try {
            const { jobServiceDS } = datasources;

            await Promise.map(jobServices, (jobService) =>
              jobServiceDS.update(jobService),
            );

            return jobServices;
          } catch (e) {
            console.error(e);
            throw e;
          }
        };

        return from(modifyJobServices()).pipe(
          map((jobServices) => {
            return setJobServices({
              jobServices: jobServices,
            });
          }),
          catchError((error) =>
            of(setJobServices({ jobServices: [] }), console.error(error)),
          ),
        );
      },
    ),
  );

const editJobServiceEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.editJobService),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { jobService },
        },
        state,
      ]) => {
        const updateJobService = async () => {
          try {
            const { jobServiceDS } = datasources;

            await jobServiceDS.update(jobService);

            return {
              jobService: jobService,
            };
          } catch (e) {
            console.error(e);
            throw e;
          }
        };

        return from(updateJobService()).pipe(
          map(({ jobService }) => {
            return setJobServices({
              jobServices: state.plugin.jobServices.map((jobServiceItem) => {
                if (jobServiceItem.id === jobService.id) {
                  return jobService;
                }
                return jobServiceItem;
              }),
            });
          }),
          catchError((error) => of(setJobServices({ jobServices: [] }))),
        );
      },
    ),
  );

const editSalesPersonEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.editSalesPerson),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { salesPerson },
        },
        state,
      ]) => {
        const updateSalesPerson = async () => {
          try {
            const { salesPersonDS } = datasources;
            await salesPersonDS.update(salesPerson); // Update the salesperson in the datasource
            return salesPerson;
          } catch (error) {
            console.error("Error editing salesperson:", error);
            throw error;
          }
        };

        return from(updateSalesPerson()).pipe(
          map((updatedSalesPerson) =>
            setSalesPersons({
              salesPersons: state.plugin.salesPersons.map((sp) =>
                sp.id === updatedSalesPerson.id ? updatedSalesPerson : sp,
              ),
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(
              setSalesPersons({ salesPersons: state.plugin.salesPersons }),
            );
          }),
        );
      },
    ),
  );

const editLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.editLocation),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { location },
        },
        state,
      ]) => {
        const updateLocation = async () => {
          try {
            const { jobLocationDS } = datasources;
            await jobLocationDS.update(location); // Update the location in the datasource
            return location;
          } catch (error) {
            console.error("Error editing location:", error);
            throw error;
          }
        };

        return from(updateLocation()).pipe(
          map((updatedLocation) =>
            setLocations({
              locations: state.plugin.locations.map((sp) =>
                sp.id === updatedLocation.id ? updatedLocation : sp,
              ),
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(setLocations({ locations: state.plugin.locations }));
          }),
        );
      },
    ),
  );

const deleteJobServiceEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteJobService),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { jobService },
        },
        state,
      ]) => {
        const deleteJobService = async () => {
          try {
            const { jobServiceDS } = datasources;

            await jobServiceDS.delete(jobService.id);

            return state.plugin.jobServices.filter(
              (jobServiceItem) => jobServiceItem.id !== jobService.id,
            );
          } catch (e) {
            console.error(e);
            throw e;
          }
        };

        return from(deleteJobService()).pipe(
          map((jobServices) => {
            return setJobServices({
              jobServices,
            });
          }),
          catchError((error) => of(setJobServices({ jobServices: [] }))),
        );
      },
    ),
  );

const deleteSalesPersonEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteSalesPerson),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id },
        },
        state,
      ]) => {
        const removeSalesPerson = async () => {
          try {
            const { salesPersonDS } = datasources;
            await salesPersonDS.delete(id); // Delete the salesperson from the datasource
            return id;
          } catch (error) {
            console.error("Error deleting salesperson:", error);
            throw error;
          }
        };

        return from(removeSalesPerson()).pipe(
          map((deletedId) =>
            setSalesPersons({
              salesPersons: state.plugin.salesPersons.filter(
                (salesPerson) => salesPerson.id !== deletedId,
              ),
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(
              setSalesPersons({ salesPersons: state.plugin.salesPersons }),
            );
          }),
        );
      },
    ),
  );

const deleteLocationEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteLocation),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id },
        },
        state,
      ]) => {
        const removeLocation = async () => {
          try {
            const { jobLocationDS } = datasources;
            await jobLocationDS.delete(id); // Delete the salesperson from the datasource
            return id;
          } catch (error) {
            console.error("Error deleting location:", error);
            throw error;
          }
        };

        return from(removeLocation()).pipe(
          map((deletedId) =>
            setLocations({
              locations: state.plugin.locations.filter(
                (location) => location.id !== deletedId,
              ),
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(setLocations({ locations: state.plugin.locations }));
          }),
        );
      },
    ),
  );

const getJobServices = ({}) =>
  new Promise((success, fail) => {
    const { jobServiceDS } = datasources;
    jobServiceDS
      .read({})
      .then((jobServices) => success(jobServices))
      .catch((error) => fail(error));
  });

const getJobServicesEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getJobServices),
    mergeMap(() => {
      return from(getJobServices({})).pipe(
        map((jobServices) => {
          return setJobServices({
            jobServices,
          });
        }),
        catchError(
          (error) =>
            console.error(error) || of(setJobServices({ jobServices: [] })),
        ),
      );
    }),
  );

const getSalesPersons = ({}) =>
  new Promise((success, fail) => {
    const { salesPersonDS } = datasources;
    salesPersonDS
      .read({})
      .then((salesPersons) => success(salesPersons))
      .catch((error) => fail(error));
  });

const getSalesPersonsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getSalesPersons),
    withLatestFrom(state$),
    mergeMap(() => {
      return from(getSalesPersons({})).pipe(
        map((salesPersons) => {
          return setSalesPersons({
            salesPersons,
          });
        }),
        catchError(
          (error) =>
            console.error(error) || of(setSalesPersons({ salesPersons: [] })),
        ),
      );
    }),
  );

const getLocations = ({}) =>
  new Promise((success, fail) => {
    const { jobLocationDS } = datasources;
    jobLocationDS
      .read({})
      .then((locations) => success(locations))
      .catch((error) => fail(error));
  });

const getLocationsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getLocations),
    withLatestFrom(state$),
    mergeMap(() => {
      return from(getLocations({})).pipe(
        map((locations) => {
          return setLocations({
            locations,
          });
        }),
        catchError(
          (error) =>
            console.error(error) || of(setLocations({ locations: [] })),
        ),
      );
    }),
  );

const cancelPluginSubscriptionEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.cancelPluginSubscription),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { subscriptionId },
        },
        state,
      ]) => {
        return from(
          cancelSubscription({
            subscriptionId,
            isSandbox: state.userSession.isSandbox,
          }),
        ).pipe(
          map(() => {
            return cancelPluginSubscriptionDone({});
          }),
          catchError((error) => {
            // The page only reloads on success, so without this a refused
            // cancellation is indistinguishable from nothing happening --
            // and the server can now refuse one, which it could not before.
            console.error(error);
            Notify.error(i18n.t("subscriptionActivation.cancelFailed"));
            return of(cancelPluginSubscriptionDone({}));
          }),
        );
      },
    ),
  );

const woocommerceCreateCustomerEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCreateCustomer),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      //Add in functionality to add order for user if nextAction was order related

      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/customers`;

      // Log the constructed URL for debugging
      console.log("WooCommerce API URL:", woocommerceUrl);

      const {
        first_name,
        last_name,
        email,
        sku = "",
        ball_name,
        manufacturer,
        old_ball_name = "",
        old_manufacturer,
        orderData,
        customerData,
        nextAction,
        spectreCustomer,
      } = payload;

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "POST",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      // Sanitize the customer payload so WC core's billing.email validation
      // never sees an empty/invalid value, regardless of which upstream path
      // built customerData.
      const safeCustomerData = ensureCustomerCreateEmail(
        customerData,
        spectreCustomer,
      );

      // Make the API call
      return from(
        fetch(urlWithOAuth, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(safeCustomerData), // Convert customer data to JSON
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        mergeMap((response) => {
          const linkWrite =
            spectreCustomer && spectreCustomer.id && response?.id
              ? of(
                  writeSpectrePosLink({
                    wcCustomerId: response.id,
                    spectreCustomerId: spectreCustomer.id,
                    spectreExtraValues: spectreCustomer.extra_values,
                  }),
                )
              : EMPTY;

          // Handle success response
          if (
            nextAction === "order" ||
            nextAction === "remove order" ||
            nextAction === "replace order"
          ) {
            return concat(
              of(woocommerceCreateCustomerDone({ customerData: response })),
              linkWrite,
              of(
                woocommerceGetProductId({
                  first_name,
                  last_name,
                  email,
                  ball_name,
                  manufacturer,
                  old_ball_name,
                  old_manufacturer,
                  orderData: { ...orderData, customer_id: response?.id },
                  customerData,
                  nextAction,
                }),
              ),
            );
          } else if (nextAction === "SKU remove") {
            return concat(
              of(woocommerceCreateCustomerDone({ customerData: response })),
              linkWrite,
              of(
                woocommerceRemoveFromOrderBySKU({
                  first_name,
                  last_name,
                  email,
                  sku,
                  ball_name,
                  manufacturer,
                  orderData: { ...orderData, customer_id: response?.id },
                  customerData,
                  nextAction,
                }),
              ),
            );
          } else if (nextAction === "SKU add") {
            return concat(
              of(woocommerceCreateCustomerDone({ customerData: response })),
              linkWrite,
              of(
                woocommerceAddToOrderFromSKU({
                  first_name,
                  last_name,
                  email,
                  sku,
                  ball_name,
                  manufacturer,
                  orderData: { ...orderData, customer_id: response?.id },
                  customerData,
                  nextAction,
                }),
              ),
            );
          } else {
            return concat(
              of(woocommerceCreateCustomerDone({ customerData: response })),
              linkWrite,
            );
          }
        }),
        catchError((error) => {
          console.warn("WooCommerce Create Customer Error", error);
          return of(
            woocommerceCreateCustomerFailure({
              message: {
                userMessage: "feedback.error.woocommerce.createCustomer",
                technicalDetail: error?.message || "Unknown error occurred",
                code: "WC_CREATE_CUSTOMER_FAILED",
                status: error?.status || error?.response?.status,
              },
            }),
          );
        }),
      );
    }),
  );

const woocommerceUpdateCustomerEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceUpdateCustomer),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Extract customer ID and data from the payload
      const { customerId, customerData } = payload;

      // Remove the `username` field from customerData
      const sanitizedCustomerData = { ...customerData };
      delete sanitizedCustomerData.username;

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/customers/${customerId}`;

      // Log the constructed URL for debugging
      console.log("WooCommerce API URL:", woocommerceUrl);

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "PUT",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      // Make the API call
      return from(
        fetch(urlWithOAuth, {
          method: "PUT", // Use PUT for updating the customer
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(sanitizedCustomerData), // Convert customer data to JSON
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Handle success response
          return woocommerceUpdateCustomerDone({ customerData: response });
        }),
        catchError((error) => {
          console.warn("WooCommerce Update Customer Error", error);
          return of({
            type: "WOOCOMMERCE_UPDATE_CUSTOMER_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

// const woocommerceDeleteCustomerEpic = (action$, state$) =>
//   action$.pipe(
//     ofType(types.woocommerceDeleteCustomer),
//     withLatestFrom(state$),
//     mergeMap(([{ payload }, state]) => {
//       // Extract WooCommerce credentials from the state
//       const consumerKey = state.plugin.plugin_settings.api_key.trim();
//       const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

//       // Extract customer ID from the payload
//       const { customerId } = payload;

//       // Construct the WooCommerce API URL
//       const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/customers/${customerId}?force=true`;

//       // Log the constructed URL for debugging
//       console.log("WooCommerce API URL:", woocommerceUrl);

//       // Initialize OAuth 1.0a
//       const oauth = new OAuth({
//         consumer: { key: consumerKey, secret: consumerSecret },
//         signature_method: "HMAC-SHA256",
//         hash_function(baseString, key) {
//           return CryptoJS.HmacSHA256(baseString, key).toString(
//             CryptoJS.enc.Base64
//           );
//         },
//       });

//       // Generate OAuth parameters
//       const requestData = {
//         url: woocommerceUrl,
//         method: "DELETE",
//       };

//       const oauthParams = oauth.authorize(requestData);

//       // Convert OAuth parameters to a query string
//       const oauthQueryString = new URLSearchParams(oauthParams).toString();

//       // Append OAuth parameters to the URL
//       const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

//       // Make the API call
//       return from(
//         fetch(urlWithOAuth, {
//           method: "DELETE", // Use DELETE for removing the customer
//           headers: {
//             "Content-Type": "application/json",
//           },
//         }).then((response) => {
//           if (!response.ok) {
//             return response.json().then((error) => {
//               throw new Error(
//                 `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`
//               );
//             });
//           }
//           return response.json(); // Parse the JSON response
//         })
//       ).pipe(
//         map((response) => {
//           // Handle success response
//           return woocommerceDeleteCustomerDone({ customerId });
//         }),
//         catchError((error) => {
//           console.warn("WooCommerce Delete Customer Error", error);
//           return of({
//             type: "WOOCOMMERCE_DELETE_CUSTOMER_FAILURE",
//             payload: {
//               message: error.message || "Unknown error occurred",
//               status: error.status || "No status code",
//               response: error.response || "No response data",
//             },
//           });
//         })
//       );
//     })
//   );

const woocommerceAddToOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceAddToOrder),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Extract order ID and order data from the payload
      const { orderId, orderData } = payload;

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/orders/${orderId}`;

      // Log the constructed URL for debugging
      console.log("WooCommerce API URL:", woocommerceUrl);

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "PUT",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      // Make the API call
      return from(
        fetch(urlWithOAuth, {
          method: "PUT", // Use PUT for updating the order
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(orderData), // Convert order data to JSON
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Handle success response
          return woocommerceAddToOrderDone({ orderData: response });
        }),
        catchError((error) => {
          console.warn("WooCommerce Add To Order Error", error);
          return of({
            type: "WOOCOMMERCE_ADD_TO_ORDER_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceRemoveFromOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceRemoveFromOrder),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Extract order ID and item to remove from the payload
      const { orderId, productNameToRemove, orderData } = payload;

      // Filter out the item to remove from the line_items array
      const updatedLineItems = orderData.line_items.filter(
        (item) => item.name.includes(productNameToRemove) === false,
      );

      // Check if the line_items array is empty
      if (updatedLineItems.length === 0) {
        // If no items remain, delete the order
        const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/orders/${orderId}?force=true`;

        // Initialize OAuth 1.0a
        const oauth = new OAuth({
          consumer: { key: consumerKey, secret: consumerSecret },
          signature_method: "HMAC-SHA256",
          hash_function(baseString, key) {
            return CryptoJS.HmacSHA256(baseString, key).toString(
              CryptoJS.enc.Base64,
            );
          },
        });

        // Generate OAuth parameters
        const requestData = {
          url: woocommerceUrl,
          method: "DELETE",
        };

        const oauthParams = oauth.authorize(requestData);

        // Convert OAuth parameters to a query string
        const oauthQueryString = new URLSearchParams(oauthParams).toString();

        // Append OAuth parameters to the URL
        const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

        // Make the API call to delete the order
        return from(
          fetch(urlWithOAuth, {
            method: "DELETE", // Use DELETE for removing the order
            headers: {
              "Content-Type": "application/json",
            },
          }).then((response) => {
            if (!response.ok) {
              return response.json().then((error) => {
                throw new Error(
                  `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
                );
              });
            }
            return response.json(); // Parse the JSON response
          }),
        ).pipe(
          map(() => {
            // Dispatch success action for deleting the order
            return woocommerceDeleteOrderDone({ orderId });
          }),
          catchError((error) => {
            console.warn("WooCommerce Delete Order Error", error);
            return of({
              type: "WOOCOMMERCE_DELETE_ORDER_FAILURE",
              payload: {
                message: error.message || "Unknown error occurred",
                status: error.status || "No status code",
                response: error.response || "No response data",
              },
            });
          }),
        );
      }

      const sanitizedLineItems = orderData.line_items.map((item) => {
        const sanitizedItem = { ...item };
        if (
          sanitizedItem.image &&
          (!sanitizedItem.image.id || isNaN(sanitizedItem.image.id))
        ) {
          sanitizedItem.image = { id: "0", src: "sample.png" };
        }
        return sanitizedItem;
      });

      // Update the line_items array to set the quantity of the item to 0
      const updatedLineItems2 = sanitizedLineItems.map((item) => {
        if (item.name.includes(productNameToRemove)) {
          return { ...item, quantity: 0, parent_name: "" }; // Set quantity to 0 for the matching item
        }
        return { ...item, parent_name: "" }; // Keep other items unchanged
      });

      const updatedOrderData = { ...orderData, line_items: updatedLineItems2 };

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/orders/${orderId}`;

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "PUT",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      // Make the API call to update the order
      return from(
        fetch(urlWithOAuth, {
          method: "PUT", // Use PUT for updating the order
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedOrderData), // Send the updated order data
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Dispatch success action for updating the order
          return woocommerceRemoveFromOrderDone({ orderData: response });
        }),
        catchError((error) => {
          console.warn("WooCommerce Remove From Order Error", error);
          return of({
            type: "WOOCOMMERCE_REMOVE_FROM_ORDER_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceCreateOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCreateOrder),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/orders`;

      // Log the constructed URL for debugging
      console.log("WooCommerce API URL:", woocommerceUrl);

      // Extract order data from the payload and tag it as Spectre-origin
      // so the POS Orders screen can show it without requiring _pos_user
      // to match the current cashier (any operator of the proshop should
      // be able to pick up an order created from Spectre).
      const orderData = {
        ...payload.orderData,
        meta_data: [
          ...(payload.orderData?.meta_data || []),
          { key: "_spectre_origin", value: "true" },
        ],
      };

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "POST",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      // Make the API call
      return from(
        fetch(urlWithOAuth, {
          method: "POST", // Use POST for creating the order
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(orderData), // Convert order data to JSON
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Handle success response
          return woocommerceCreateOrderDone({ orderData: response });
        }),
        catchError((error) => {
          console.warn("WooCommerce Create Order Error", error);
          return of({
            type: "WOOCOMMERCE_CREATE_ORDER_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceCheckUserExistsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckUserExists),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const apiBase = state.plugin.plugin_settings.api_url;

      const { first_name, last_name, email, spectreCustomer } = payload;

      // Pick the strongest available search key. WCPOS' enhanced search
      // matches across email, login, first_name, last_name, billing_*.
      const emailTrim = (email || "").trim();
      const lastTrim = (last_name || "").trim();
      const searchKey = emailTrim || lastTrim;

      // Search-or-create path. Used when there's no stored link, or when the
      // stored link is stale (its WC customer no longer exists on this site).
      const searchAndResolve = () => {
        if (!searchKey) {
          // Nothing to search by — fall straight to create. The link is written
          // back in woocommerceCreateCustomerEpic on success.
          return of(woocommerceCreateCustomer(payload));
        }

        // Search extension to billing fields lives in the spectrepos-customer-link
        // mu-plugin (filter on woocommerce_rest_customer_query). We stay on the
        // standard wc/v3 namespace — wcpos/v1 routes aren't always registered.
        const baseUrl = `${apiBase}/wp-json/wc/v3/customers?per_page=20&search=${encodeURIComponent(searchKey)}`;
        const urlWithOAuth = signWoocommerceUrl(
          baseUrl,
          "GET",
          consumerKey,
          consumerSecret,
        );

        return from(
          fetch(urlWithOAuth, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }).then((response) => {
            if (!response.ok) {
              return response.json().then((error) => {
                throw new Error(
                  `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
                );
              });
            }
            return response.json();
          }),
        ).pipe(
          mergeMap((response) => {
            const candidates = filterWcCandidates(response, {
              first_name,
              last_name,
              email,
              telephone: spectreCustomer?.telephone,
            });

            if (candidates.length === 1) {
              return buildPostMatchActions(candidates[0].id, payload);
            }

            if (candidates.length > 1) {
              return of(
                woocommerceMatchesFound({
                  matches: candidates,
                  pendingPayload: payload,
                }),
              );
            }

            // 0 candidates — fall through to create. The link is written back
            // after create in woocommerceCreateCustomerEpic.
            return concat(
              of({
                type: "WOOCOMMERCE_CHECK_USER_EXISTS_FAILURE",
                payload: { error: "Customer not found" },
              }),
              of(woocommerceCreateCustomer(payload)),
            );
          }),
          catchError((error) => {
            console.warn("WooCommerce Check User Exists Error", error);
            return of({
              type: "WOOCOMMERCE_CHECK_USER_EXISTS_FAILURE",
              payload: {
                message: error.message || "Unknown error occurred",
                status: error.status || "No status code",
                response: error.response || "No response data",
              },
            });
          }),
        );
      };

      // Shortcut: if the v3 customer carries a stored POS link, use it — but
      // validate the WC customer still exists first. A stale id would make the
      // order create fail with woocommerce_rest_invalid_customer_id; in that
      // case re-resolve (search/create) which rewrites the link to a valid id.
      const storedLink = getPosLinkFromSpectre(spectreCustomer);
      if (storedLink) {
        return wcCustomerExists(
          apiBase,
          consumerKey,
          consumerSecret,
          storedLink.customer_id,
        ).pipe(
          mergeMap((ok) =>
            ok
              ? buildPostMatchActions(storedLink.customer_id, payload)
              : searchAndResolve(),
          ),
        );
      }

      return searchAndResolve();
    }),
  );

const woocommerceMatchResolvedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceMatchResolved),
    withLatestFrom(state$),
    mergeMap(([incoming, state]) => {
      const { payload } = incoming;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, incoming);
      if (off) return off;
      const { action, wcCustomerId, originalPayload } = payload;
      if (!originalPayload) return EMPTY;
      if (action === "link" && wcCustomerId) {
        return buildPostMatchActions(wcCustomerId, originalPayload);
      }
      if (action === "create") {
        return of(woocommerceCreateCustomer(originalPayload));
      }
      return EMPTY;
    }),
  );

const writeSpectrePosLinkEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.writeSpectrePosLink),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const { wcCustomerId, spectreCustomerId } = payload;
      if (!wcCustomerId || !spectreCustomerId) return EMPTY;

      // Both PUT/POST are idempotent — we issue them unconditionally so
      // that a previously-failed write on either side (e.g. WC 403 during
      // an earlier attempt) gets reconciled the next time the bowler's
      // profile is opened.

      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const apiBase = state.plugin.plugin_settings.api_url;

      // Write the link on the WC side via the dedicated spectrepos endpoint.
      // We can't PUT /wc/v3/customers/{id} because WC's permission check calls
      // current_user_can('edit_user') which in WP multisite requires
      // is_super_admin() — subsite admins (the OAuth key owners) hit a 403.
      // The mu-plugin spectrepos-customer-link.php exposes a write that's
      // scoped to just this one meta key.
      const wcUrl = signWoocommerceUrl(
        `${apiBase}/wp-json/wc/v3/spectre-customer-link/${wcCustomerId}`,
        "POST",
        consumerKey,
        consumerSecret,
      );
      const wcPut = fetch(wcUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          spectre_customer_id: spectreCustomerId,
        }),
      }).catch((e) => {
        console.warn("WC link write failed", e);
        return null;
      });

      // Write the link on the v3 side (extra_values.pos.customer_id).
      const v3Put = api
        .ajaxSession(`customer/${spectreCustomerId}/pos-link`, {
          type: "PUT",
          data: { wc_customer_id: wcCustomerId },
        })
        .catch((e) => {
          console.warn("v3 pos-link write failed", e);
          return null;
        });

      return from(Promise.all([wcPut, v3Put])).pipe(
        map(() =>
          writeSpectrePosLinkDone({ wcCustomerId, spectreCustomerId }),
        ),
        catchError((e) => {
          console.warn("writeSpectrePosLink error", e);
          return of(
            writeSpectrePosLinkDone({ wcCustomerId, spectreCustomerId }),
          );
        }),
      );
    }),
  );

// Validates the POS link for a v3 customer when their profile is opened.
// Silent on 0 matches and on already-linked customers. Auto-writes the link
// on a single match, opens the picker (link-only mode) on multiple.
const checkSpectrePosLinkEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.checkSpectrePosLink),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const spectreCustomer = payload?.spectreCustomer;
      if (!spectreCustomer || !spectreCustomer.id) return EMPTY;

      const subs = state.plugin.pluginSubscriptions || [];
      if (subs.indexOf("SPECTRE_POS") < 0) return EMPTY;

      const apiBase = state.plugin.plugin_settings.api_url;
      const consumerKey = (state.plugin.plugin_settings.api_key || "").trim();
      const consumerSecret = (
        state.plugin.plugin_settings.api_secret || ""
      ).trim();
      if (!apiBase || !consumerKey || !consumerSecret) return EMPTY;

      const emailTrim = (spectreCustomer.email || "").trim();
      const lastTrim = (spectreCustomer.lastname || "").trim();
      const searchKey = emailTrim || lastTrim;

      // Search-or-create path. Used when there's no stored link, or when the
      // stored link is stale (its WC customer no longer exists on this site).
      const searchAndResolve = () => {
        if (!searchKey) return EMPTY;

        // Search extension to billing fields lives in the spectrepos-customer-link
        // mu-plugin (filter on woocommerce_rest_customer_query). We stay on the
        // standard wc/v3 namespace — wcpos/v1 routes aren't always registered.
        const baseUrl = `${apiBase}/wp-json/wc/v3/customers?per_page=20&search=${encodeURIComponent(searchKey)}`;
        const urlWithOAuth = signWoocommerceUrl(
          baseUrl,
          "GET",
          consumerKey,
          consumerSecret,
        );

        return from(
          fetch(urlWithOAuth, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }).then((response) => {
            if (!response.ok) return [];
            return response.json();
          }),
        ).pipe(
          mergeMap((response) => {
            const candidates = filterWcCandidates(response, {
              first_name: spectreCustomer.firstname,
              last_name: spectreCustomer.lastname,
              email: spectreCustomer.email,
              telephone: spectreCustomer.telephone,
            });

            if (candidates.length === 1) {
              return of(
                writeSpectrePosLink({
                  wcCustomerId: candidates[0].id,
                  spectreCustomerId: spectreCustomer.id,
                  spectreExtraValues: spectreCustomer.extra_values,
                }),
              );
            }

            if (candidates.length > 1) {
              return of(
                woocommerceMatchesFound({
                  matches: candidates,
                  pendingPayload: { spectreCustomer, mode: "background" },
                }),
              );
            }

            // 0 candidates → auto-create the WC customer so the bowler is
            // immediately linkable for orders / SKU operations without the
            // proshop having to do anything manual. The create epic writes
            // the link back to both sides after the POST succeeds.
            return of(
              woocommerceCreateCustomer({
                first_name: spectreCustomer.firstname || "",
                last_name: spectreCustomer.lastname || "",
                email: spectreCustomer.email || "",
                customerData: buildAutoCreateCustomerData(spectreCustomer),
                spectreCustomer: spectreCustomer,
                nextAction: undefined,
              }),
            );
          }),
          catchError((error) => {
            console.warn("checkSpectrePosLink error", error);
            return EMPTY;
          }),
        );
      };

      // If v3 already has a stored link, validate the WC customer still exists.
      // If it does, re-issue the write to keep WC in sync (idempotent; the WC
      // write may have failed before, leaving one side linked). If it's stale
      // (id deleted / wrong site), re-resolve so the link is rewritten to a
      // valid id — otherwise the pill shows but every order op 400s with
      // woocommerce_rest_invalid_customer_id.
      const storedLink = getPosLinkFromSpectre(spectreCustomer);
      if (storedLink) {
        return wcCustomerExists(
          apiBase,
          consumerKey,
          consumerSecret,
          storedLink.customer_id,
        ).pipe(
          mergeMap((ok) =>
            ok
              ? of(
                  writeSpectrePosLink({
                    wcCustomerId: storedLink.customer_id,
                    spectreCustomerId: spectreCustomer.id,
                    spectreExtraValues: spectreCustomer.extra_values,
                  }),
                )
              : searchAndResolve(),
          ),
        );
      }

      return searchAndResolve();
    }),
  );

// On a hard refresh the customer can finish loading (getCustomer →
// checkSpectrePosLink) BEFORE the plugin settings/subscriptions arrive, so
// that first check bails on missing creds (api_key/secret/url) and the
// SpectrePOS pill never renders — and no market call is ever made. When the
// settings finally land (getPluginSubscriptionsDone populates both subs and
// the WC creds), re-run the check for the currently-selected customer so the
// link is reconciled and the pill appears. Harmless when no customer is
// selected or when the original check already succeeded (idempotent).
const recheckSpectrePosLinkOnSettingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getPluginSubscriptionsDone),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const subs = state.plugin.pluginSubscriptions || [];
      const ps = state.plugin.plugin_settings || {};
      const sc = state.bowlers.selectedCustomer;
      if (
        !posSendEnabled(state) ||
        subs.indexOf("SPECTRE_POS") < 0 ||
        !ps.api_key ||
        !ps.api_secret ||
        !ps.api_url ||
        !sc ||
        !sc.id
      ) {
        return EMPTY;
      }
      return of(checkSpectrePosLink({ spectreCustomer: sc }));
    }),
  );

const woocommerceCheckSKUExistsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckSKUExists), // Listen for the action type
    withLatestFrom(state$), // Combine the action with the current state
    mergeMap(([action, state]) => {
      const { payload } = action;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, action);
      if (off) return off;
      // Extract SKU from the payload
      const { sku } = payload;

      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Construct the WooCommerce API URL with the SKU query parameter
      const baseUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/products`;
      const queryParams = `sku=${encodeURIComponent(sku)}`; // Search by SKU
      const woocommerceUrl = `${baseUrl}?${queryParams}`;

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "GET",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

      // Make the API call to fetch all products
      return from(
        fetch(urlWithOAuth, {
          method: "GET", // Use GET to retrieve the list of products
          headers: {
            "Content-Type": "application/json",
          },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Check if any product matches the SKU
          const productExists = response.some((product) => product.sku === sku);

          return woocommerceCheckSKUExistsDone({ skuExists: productExists });
        }),
        catchError((error) => {
          console.warn("WooCommerce Check SKU Exists Error", error);
          return of({
            type: "WOOCOMMERCE_CHECK_SKU_EXISTS_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const buildOauthGetUrl = (
  apiUrl,
  consumerKey,
  consumerSecret,
  path,
  extraParams = {},
) => {
  const oauth = new OAuth({
    consumer: { key: consumerKey, secret: consumerSecret },
    signature_method: "HMAC-SHA256",
    hash_function(baseString, key) {
      return CryptoJS.HmacSHA256(baseString, key).toString(
        CryptoJS.enc.Base64,
      );
    },
  });
  const url = `${apiUrl}${path}`;
  // OAuth 1.0a signs every query param, so non-oauth params (e.g. `status`)
  // must be passed as `data` to be folded into the signature base string,
  // then re-appended to the final URL alongside the oauth_* params.
  const oauthParams = oauth.authorize({ url, method: "GET", data: extraParams });
  const allParams = { ...extraParams, ...oauthParams };
  return `${url}?${new URLSearchParams(allParams).toString()}`;
};

const fetchProductBySku = (apiUrl, consumerKey, consumerSecret, sku) => {
  const url = buildOauthGetUrl(
    apiUrl,
    consumerKey,
    consumerSecret,
    `/wp-json/wc/v3/spectre-product-by-sku/${encodeURIComponent(sku)}`,
  );
  return fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  }).then((response) => {
    if (response.status === 404) return null;
    if (!response.ok) {
      return response.json().then((error) => {
        throw new Error(
          `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
        );
      });
    }
    return response.json();
  });
};

const productToLineItem = (product, quantity = 1) => {
  if (!product || !product.id) return null;
  const isVariation = product.type === "variation" && product.parent_id;
  const item = {
    product_id: isVariation ? product.parent_id : product.id,
    quantity,
  };
  if (isVariation) item.variation_id = product.id;
  return item;
};

const woocommerceAddToOrderFromSKUEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceAddToOrderFromSKU), // Listen for the action type
    withLatestFrom(state$), // Combine the action with the current state
    mergeMap(([{ payload }, state]) => {
      // Extract SKU and other payload data
      const {
        first_name,
        last_name,
        email,
        sku,
        orderData,
        customerData,
        nextAction,
        insertSkus, // optional string[] of finger/thumb insert SKUs to add as extra line items
        replaceMode, // bool — replace existing ball line item instead of appending
        existingBallLineItemIds, // optional number[] of line_item ids to remove on replace
      } = payload;

      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const apiUrl = state.plugin.plugin_settings.api_url;

      // Resolve ball SKU + every insert SKU in parallel. The custom
      // spectre-product-by-sku endpoint wraps wc_get_product_id_by_sku so
      // variation SKUs resolve too (stock /wc/v3/products?sku= filters them
      // out).
      const skusToResolve = [sku, ...(insertSkus || [])];
      return forkJoin(
        skusToResolve.map((s) =>
          from(fetchProductBySku(apiUrl, consumerKey, consumerSecret, s)),
        ),
      ).pipe(
        mergeMap((products) => {
          const ballProduct = products[0];
          if (!ballProduct || !ballProduct.id) {
            console.warn(
              "[spectrepos] ball SKU not in proshop catalog:",
              sku,
            );
            return of({
              type: "WOOCOMMERCE_ADD_TO_ORDER_FROM_SKU_FAILURE",
              payload: { error: "Product not found", missingSku: sku },
            });
          }
          const ballItem = productToLineItem(ballProduct);
          const insertItems = [];
          const missingInsertSkus = [];
          (insertSkus || []).forEach((insertSku, i) => {
            const product = products[1 + i];
            const item = productToLineItem(product);
            if (item) {
              insertItems.push(item);
            } else {
              missingInsertSkus.push(insertSku);
            }
          });
          if (missingInsertSkus.length > 0) {
            console.warn(
              "[spectrepos] inserts dropped — SKUs not in proshop catalog:",
              missingInsertSkus,
            );
          }
          return concat(
            of(
              woocommerceAddToOrderFromSKUDone({
                productId: ballItem.product_id,
                variationId: ballItem.variation_id || null,
                missingInsertSkus,
              }),
            ),
            of(
              woocommerceCheckOrderExists({
                first_name,
                last_name,
                email,
                orderData,
                customerData,
                productId: ballItem.product_id,
                variationId: ballItem.variation_id || null,
                extraLineItems: insertItems,
                replaceMode: !!replaceMode,
                existingBallLineItemIds: existingBallLineItemIds || [],
                nextAction,
              }),
            ),
          );
        }),
        catchError((error) => {
          console.warn("WooCommerce Add to Order from SKU Error", error);
          return of({
            type: "WOOCOMMERCE_ADD_TO_ORDER_FROM_SKU_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceRemoveFromOrderBySKUEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceRemoveFromOrderBySKU), // Listen for the action type
    withLatestFrom(state$), // Combine the action with the current state
    mergeMap(([{ payload }, state]) => {
      // Extract SKU and other payload data
      const {
        first_name,
        last_name,
        email,
        sku,
        orderData,
        customerData,
        nextAction,
      } = payload;

      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Construct the WooCommerce API URL with the SKU query parameter
      const baseUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/products`;
      const queryParams = `sku=${encodeURIComponent(sku)}`;
      const woocommerceUrl = `${baseUrl}?${queryParams}`;

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "GET",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

      // Make the API call to fetch the product by SKU
      return from(
        fetch(urlWithOAuth, {
          method: "GET", // Use GET to retrieve the product by SKU
          headers: {
            "Content-Type": "application/json",
          },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        mergeMap((response) => {
          // Find the product that matches the SKU
          const product = response.find((p) => p.sku === sku);
          const ball_name = product.name;

          // If a matching product is found, dispatch the next actions
          if (product) {
            return concat(
              of(
                woocommerceRemoveFromOrderBySKUDone({
                  productId: product.id,
                }),
              ),
              of(
                woocommerceCheckOrderExists({
                  first_name,
                  last_name,
                  email,
                  orderData,
                  ball_name: ball_name,
                  customerData,
                  productId: product.id,
                  nextAction,
                }),
              ),
            );
          } else {
            // If no match is found, dispatch a failure action
            return of({
              type: "WOOCOMMERCE_REMOVE_FROM_ORDER_BY_SKU_FAILURE",
              payload: { error: "Product not found" },
            });
          }
        }),
        catchError((error) => {
          console.warn("WooCommerce Remove from Order by SKU Error", error);
          return of({
            type: "WOOCOMMERCE_REMOVE_FROM_ORDER_BY_SKU_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceDeleteOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceDeleteOrder),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Extract order ID from the payload
      const { orderId } = payload;

      // Construct the WooCommerce API URL
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/orders/${orderId}?force=true`;

      // Log the constructed URL for debugging
      console.log("WooCommerce API URL:", woocommerceUrl);

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "DELETE",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

      // Make the API call
      return from(
        fetch(urlWithOAuth, {
          method: "DELETE", // Use DELETE for removing the order
          headers: {
            "Content-Type": "application/json",
          },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        map((response) => {
          // Handle success response
          return woocommerceDeleteOrderDone({ orderId });
        }),
        catchError((error) => {
          console.warn("WooCommerce Delete Order Error", error);
          return of({
            type: "WOOCOMMERCE_DELETE_ORDER_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceCheckOrderExistsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckOrderExists), // Listen for the action type
    withLatestFrom(state$), // Combine the action with the current state
    mergeMap(([{ payload }, state]) => {
      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const apiUrl = state.plugin.plugin_settings.api_url;

      //Modify the above later to include a search so that it does not run into the list maximum.

      // Extract first_name, last_name, email, and ball_name from the payload
      const {
        first_name,
        last_name,
        email,
        ball_name,
        manufacturer,
        old_ball_name,
        old_manufacturer,
        orderData,
        customerData,
        productId,
        variationId,
        extraLineItems, // optional [{product_id, variation_id?, quantity}] (inserts)
        replaceMode,
        existingBallLineItemIds,
        nextAction,
      } = payload;

      const buildLineItem = () => {
        const item = { product_id: productId, quantity: 1 };
        if (variationId) item.variation_id = variationId;
        return item;
      };

      const buildLineItems = () => [
        buildLineItem(),
        ...((extraLineItems || []).map((it) => ({ ...it, quantity: it.quantity || 1 }))),
      ];

      // Only operate on the live POS cart (wcpos `pos-open` status). Paid /
      // completed orders must never be matched here — otherwise Add/Replace
      // would target a finished order and the replace removal would no-op.
      const urlWithOAuth = buildOauthGetUrl(
        apiUrl,
        consumerKey,
        consumerSecret,
        `/wp-json/wc/v3/orders`,
        { status: "pos-open" },
      );

      // Make the API call to fetch all orders
      return from(
        fetch(urlWithOAuth, {
          method: "GET", // Use GET to retrieve the list of orders
          headers: {
            "Content-Type": "application/json",
          },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        mergeMap((response) => {
          // Filter the orders based on first_name, last_name, email, and ball_name
          const order = response.find((o) => {
            const billing = o.billing || {};
            return (
              billing.first_name.toLowerCase() === first_name.toLowerCase() &&
              billing.last_name.toLowerCase() === last_name.toLowerCase() &&
              billing.email.toLowerCase() === email.toLowerCase()
            );
          });

          // If a matching order is found, dispatch the success action
          if (order) {
            if (nextAction === "order" || nextAction === "SKU add") {
              // When replaceMode, zero-out the existing ball line items (and
              // any prior inserts? — out of scope for now, only the ball is
              // identified). WC REST removes line items by setting quantity:0
              // on the existing line_item id within the array.
              const removalItems =
                replaceMode && existingBallLineItemIds?.length
                  ? existingBallLineItemIds.map((id) => ({ id, quantity: 0 }))
                  : [];
              return concat(
                of(
                  woocommerceCheckOrderExistsDone({
                    orderId: order.id,
                  }),
                ),
                of(
                  woocommerceAddToOrder({
                    orderId: order.id,
                    orderData: {
                      ...orderData,
                      line_items: [...removalItems, ...buildLineItems()],
                    },
                  }),
                ),
              );
            } else if (
              nextAction === "remove order" ||
              nextAction === "SKU remove"
            ) {
              return concat(
                of(
                  woocommerceCheckOrderExistsDone({
                    orderId: order.id,
                  }),
                ),
                of(
                  woocommerceRemoveFromOrder({
                    orderId: order.id,
                    productNameToRemove: ball_name,
                    orderData: order,
                  }),
                ),
              );
            } else if (nextAction === "replace order") {
              return concat(
                of(
                  woocommerceCheckOrderExistsDone({
                    orderId: order.id,
                  }),
                ),
                of(
                  woocommerceReplaceOrder({
                    first_name,
                    last_name,
                    email,
                    ball_name,
                    manufacturer,
                    orderData: order,
                    customerData,
                    old_ball_name,
                    old_manufacturer,
                    newItem: buildLineItem(),
                  }),
                ),
              );
            }
          } else {
            // If no match is found, dispatch a failure action and creat a new order while checking if user needs to be created
            return concat(
              of({
                type: "WOOCOMMERCE_CHECK_ORDER_EXISTS_FAILURE",
                payload: { error: "Order not found" },
              }),
              of(
                woocommerceCreateOrder({
                  orderData: {
                    ...orderData,
                    line_items: buildLineItems(),
                  },
                }),
              ),
            );
          }
        }),
        catchError((error) => {
          console.warn("WooCommerce Check Order Exists Error", error);
          return of({
            type: "WOOCOMMERCE_CHECK_ORDER_EXISTS_FAILURE",
            payload: {
              message: error.message || "Unknown error occurred",
              status: error.status || "No status code",
              response: error.response || "No response data",
            },
          });
        }),
      );
    }),
  );

const woocommerceGetProductIdEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceGetProductId), // Listen for the action type
    withLatestFrom(state$), // Combine the action with the current state
    mergeMap(([{ payload }, state]) => {
      // Extract ball_name from the payload
      const {
        first_name,
        last_name,
        email,
        ball_name,
        manufacturer,
        old_ball_name,
        old_manufacturer,
        orderData,
        customerData,
        nextAction,
      } = payload;

      // Extract WooCommerce credentials from the state
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();

      // Construct the WooCommerce API URL
      const baseUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/products`;
      const queryParams = `search=${encodeURIComponent(ball_name)}`;
      const woocommerceUrl = `${baseUrl}?${queryParams}`;

      // Initialize OAuth 1.0a
      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      // Generate OAuth parameters
      const requestData = {
        url: woocommerceUrl,
        method: "GET",
      };

      const oauthParams = oauth.authorize(requestData);

      // Convert OAuth parameters to a query string
      const oauthQueryString = new URLSearchParams(oauthParams).toString();

      // Append OAuth parameters to the URL
      const urlWithOAuth = `${woocommerceUrl}&${oauthQueryString}`;

      // Make the API call to fetch all products
      return from(
        fetch(urlWithOAuth, {
          method: "GET", // Use GET to retrieve the list of products
          headers: {
            "Content-Type": "application/json",
          },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json(); // Parse the JSON response
        }),
      ).pipe(
        mergeMap((response) => {
          // Filter the products based on ball_name
          const product = response.find(
            (p) =>
              p.name.toLowerCase().includes(ball_name.toLowerCase()) &&
              p.name.toLowerCase().includes(manufacturer.toLowerCase()),
          );

          // If a matching product is found, dispatch the success action
          if (product) {
            return concat(
              of(
                woocommerceGetProductIdDone({
                  productId: product.id,
                }),
              ),
              of(
                woocommerceCheckOrderExists({
                  first_name,
                  last_name,
                  email,
                  ball_name,
                  manufacturer,
                  old_ball_name,
                  old_manufacturer,
                  orderData,
                  customerData,
                  productId: product.id,
                  nextAction,
                }),
              ),
            );
          } else {
            // If no match is found, dispatch a failure action
            return of(
              woocommerceGetProductIdFailure({
                message: {
                  userMessage: "feedback.error.woocommerce.productNotFound",
                  code: "PRODUCT_NOT_FOUND",
                },
              }),
            );
            //If this failure is triggered specifically, there should be an alert that tells the user the product can't be found on woocommerce and to update their database instance/redo the import
          }
        }),
        catchError((error) => {
          console.warn("WooCommerce Get Product ID Error", error);
          return of(
            woocommerceGetProductIdFailure({
              message: {
                userMessage: "feedback.error.woocommerce.getProduct",
                technicalDetail: error?.message || "Unknown error occurred",
                code: "WC_GET_PRODUCT_FAILED",
                status: error?.status,
              },
            }),
          );
        }),
      );
    }),
  );

const woocommerceReplaceOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceReplaceOrder),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      // Extract data from the payload
      const {
        first_name,
        last_name,
        email,
        ball_name,
        manufacturer,
        orderData,
        customerData,
        old_ball_name,
        old_manufacturer,
        newItem, // Contains product_id and quantity
      } = payload;

      // Dispatch two actions:
      // 1. Add the new item to the order
      // 2. Remove the old item from the order
      return concat(
        of(
          woocommerceAddToOrder({
            orderId: orderData.id,
            orderData: {
              ...orderData,
              line_items: [newItem], // Add the new item
            },
          }),
        ),
        of(
          woocommerceGetProductId({
            first_name,
            last_name,
            email,
            ball_name: old_ball_name, // Use old_ball_name to find the product to remove
            manufacturer: old_manufacturer,
            orderData,
            customerData,
            nextAction: "remove order", // Set nextAction to "remove order"
          }),
        ),
      );
    }),
  );

const woocommerceCreateProductEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCreateProduct),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const { jobService } = payload;
      const { name, price, sku } = jobService;

      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const woocommerceUrl = `${state.plugin.plugin_settings.api_url}/wp-json/wc/v3/products`;

      const oauth = new OAuth({
        consumer: { key: consumerKey, secret: consumerSecret },
        signature_method: "HMAC-SHA256",
        hash_function(baseString, key) {
          return CryptoJS.HmacSHA256(baseString, key).toString(
            CryptoJS.enc.Base64,
          );
        },
      });

      const oauthParams = oauth.authorize({
        url: woocommerceUrl,
        method: "POST",
      });
      const oauthQueryString = new URLSearchParams(oauthParams).toString();
      const urlWithOAuth = `${woocommerceUrl}?${oauthQueryString}`;

      const productBody = {
        name,
        type: "simple",
        regular_price: String(price ?? ""),
        sku,
        virtual: true,
        manage_stock: false,
        status: "publish",
        tax_status: "taxable",
        catalog_visibility: "hidden",
      };

      return from(
        fetch(urlWithOAuth, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productBody),
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json();
        }),
      ).pipe(
        mergeMap(() =>
          concat(
            of(woocommerceCreateProductDone()),
            of(addJobService({ jobService })),
          ),
        ),
        catchError((error) => {
          console.warn("WooCommerce Create Product Error", error);
          return of(
            woocommerceCreateProductFailure({
              message: error.message || "Failed to create product",
            }),
          );
        }),
      );
    }),
  );

const provisionSpectrePOSEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.provisionSpectrePOS),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      from(
        provisionSpectrePOSSite({
          userId: state.userSession.userData.id,
          isSandbox: state.userSession.isSandbox,
        }),
      ).pipe(
        mergeMap((result) => {
          const rawStatus =
            result.status || result.provision_status || "syncing";
          const status =
            rawStatus === "syncing_catalog" ? "syncing" : rawStatus;
          const actions = [
            of(
              provisionSpectrePOSDone({
                status,
                backend: result.backend || null,
                progress: result.progress,
                ownerUsername: result.owner_username || null,
                ownerPassword: result.owner_password || null,
              }),
            ),
          ];
          // Update plugin_settings with new API keys
          if (result.api_url || result.consumer_key) {
            actions.push(
              of(
                setPluginSettings({
                  plugin_settings: {
                    ...state.plugin.plugin_settings,
                    api_url: result.api_url || "",
                    api_key: result.consumer_key || "",
                    api_secret: result.consumer_secret || "",
                  },
                  provisionStatus: status,
                }),
              ),
            );
          }
          actions.push(of(getProvisionStatusAction()));
          return concat(...actions);
        }),
        catchError((error) => {
          console.warn("provisionSpectrePOS Error", error);
          return of(
            provisionSpectrePOSError({
              message: error.message || "Provision failed",
            }),
          );
        }),
      ),
    ),
  );

const pollProvisionStatusEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getProvisionStatus),
    switchMap(() =>
      interval(5000).pipe(
        startWith(0),
        takeUntil(action$.pipe(ofType(types.stopProvisionPolling))),
        withLatestFrom(state$),
        mergeMap(([_, state]) =>
          from(
            fetchProvisionStatus({
              userId: state.userSession.userData.id,
              isSandbox: state.userSession.isSandbox,
            }),
          ).pipe(
            mergeMap((result) => {
              const rawStatus = result.status || result.provision_status;
              const status =
                rawStatus === "syncing_catalog" ? "syncing" : rawStatus;
              // Chentro reports the catalog as a product count, not steps.
              const progress =
                result.backend === "hbs"
                  ? { products: result.catalog?.product_count ?? null }
                  : result.sync_progress || result.progress;
              if (status === "not_found") {
                // Race: ProvisioningProgress's useEffect dispatches
                // getProvisionStatus() on mount, which fires immediately
                // (startWith(0)). Right after Paddle's onComplete the
                // provisionSpectrePOS API call is still in flight and the
                // WP site doesn't exist yet, so this poll legitimately
                // returns "not_found" — but resetting provisionStatus to
                // null here would un-mount ProvisioningProgress, drop
                // activatingPluginType, and bounce the UI back to the
                // Subscribe button + dropdown for a moment (a confused
                // user could re-pay). When state.plugin.provisioning is
                // true, the API call is mid-flight; skip this tick and
                // let the next 5s poll catch the real status once
                // provisionSpectrePOSDone fires.
                if (state.plugin.provisioning) {
                  return EMPTY;
                }
                return concat(
                  of(setProvisionStatus({ status: null, progress: null })),
                  of(stopProvisionPolling()),
                );
              }
              if (status === "ready") {
                // Trigger an immediate site-status fetch so the
                // SpectreposStatusPanel populates without waiting for
                // its own 30s polling tick. Refresh pluginSubscriptions
                // because the initial fetch right after Paddle's
                // onComplete returns before the Paddle webhook has
                // processed on the backend, so SPECTRE_POS isn't in the
                // list yet — without this refresh, the Subscribe button
                // re-appears when provisioning finishes (and a confused
                // user could pay twice). Also stop the 5s provision
                // poll — we're done.
                return concat(
                  of(
                    setProvisionStatus({
                      status: "ready",
                      progress,
                      backend: result.backend,
                    }),
                  ),
                  of(getSpectreposSiteStatus()),
                  of(getPluginSubscriptionsAction()),
                  of(stopProvisionPolling()),
                );
              }
              if (status === "error") {
                // Nothing will change on its own: stop asking. The stepper's
                // retry button starts the provision (and the polling) again.
                return concat(
                  of(
                    setProvisionStatus({
                      status,
                      progress,
                      backend: result.backend,
                    }),
                  ),
                  of(stopProvisionPolling()),
                );
              }
              return of(
                setProvisionStatus({ status, progress, backend: result.backend }),
              );
            }),
            catchError((error) => {
              console.warn("pollProvisionStatus Error", error);
              return EMPTY;
            }),
          ),
        ),
      ),
    ),
  );

const getSpectreposSiteStatusEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getSpectreposSiteStatus),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      from(
        fetchSpectreposSiteStatus({
          userId: state.userSession.userData.id,
          isSandbox: state.userSession.isSandbox,
        }),
      ).pipe(
        map((siteStatus) => setSpectreposSiteStatus({ siteStatus })),
        catchError((error) => {
          console.warn("getSpectreposSiteStatus Error", error);
          // Set a sentinel so the panel can show an error state instead of
          // staying on "Loading…" forever. The polling useEffect will retry
          // on its next 30s tick.
          return of(
            setSpectreposSiteStatus({
              siteStatus: {
                status: "error",
                error: error?.message || "Failed to load site status",
              },
            }),
          );
        }),
      ),
    ),
  );

const woocommerceCheckBallInOpenOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckBallInOpenOrder),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      // A shop on HBS is answered by plugin.hbs.epic.js.
      if (isHbsShop(state)) return EMPTY;
      // "Send to SpectrePOS" turned off in Settings.
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const { first_name, last_name, email, ball_name } = payload;
      const consumerKey = state.plugin.plugin_settings.api_key.trim();
      const consumerSecret = state.plugin.plugin_settings.api_secret.trim();
      const apiUrl = state.plugin.plugin_settings.api_url;
      const urlWithOAuth = buildOauthGetUrl(
        apiUrl,
        consumerKey,
        consumerSecret,
        `/wp-json/wc/v3/orders`,
        // Only the live POS cart (wcpos `pos-open` status) is editable from
        // Spectre. Without this, paid/completed orders matched too — so we'd
        // prompt Add/Replace against a finished order the customer already
        // paid for, and the replace would silently no-op.
        { status: "pos-open" },
      );
      return from(
        fetch(urlWithOAuth, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }).then((response) => {
          if (!response.ok) {
            return response.json().then((error) => {
              throw new Error(
                `${error.message} (Code: ${error.code}, Status: ${error.data?.status})`,
              );
            });
          }
          return response.json();
        }),
      ).pipe(
        map((orders) => {
          const order = (orders || []).find((o) => {
            const billing = o.billing || {};
            return (
              (billing.first_name || "").toLowerCase() ===
                (first_name || "").toLowerCase() &&
              (billing.last_name || "").toLowerCase() ===
                (last_name || "").toLowerCase() &&
              (billing.email || "").toLowerCase() ===
                (email || "").toLowerCase()
            );
          });
          if (!order) {
            return woocommerceCheckBallInOpenOrderDone({
              ball_name,
              found: false,
              orderId: null,
              ballInOrder: false,
              ballLineItemIds: [],
            });
          }
          const target = (ball_name || "").toLowerCase();
          const ballLineItemIds = (order.line_items || [])
            .filter((it) => (it.name || "").toLowerCase().includes(target))
            .map((it) => it.id);
          return woocommerceCheckBallInOpenOrderDone({
            ball_name,
            found: true,
            orderId: order.id,
            ballInOrder: ballLineItemIds.length > 0,
            ballLineItemIds,
          });
        }),
        catchError((error) => {
          console.warn("WooCommerce Check Ball in Open Order Error", error);
          return of(
            woocommerceCheckBallInOpenOrderDone({
              ball_name,
              found: false,
              orderId: null,
              ballInOrder: false,
              ballLineItemIds: [],
              error: error.message || "Unknown error",
            }),
          );
        }),
      );
    }),
  );

export default combineEpics(
  getPluginsEpic,
  subscribePluginEpic,
  getPluginSubscriptionsEpic,
  addJobServiceEpic,
  addSalesPersonEpic,
  addLocationEpic,
  editJobServiceEpic,
  editSalesPersonEpic,
  editLocationEpic,
  updateJobServicesEpic,
  getJobServicesEpic,
  getSalesPersonsEpic,
  getLocationsEpic,
  deleteJobServiceEpic,
  deleteSalesPersonEpic,
  deleteLocationEpic,
  woocommerceCreateCustomerEpic,
  woocommerceUpdateCustomerEpic,
  woocommerceAddToOrderEpic,
  woocommerceCreateOrderEpic,
  woocommerceCheckUserExistsEpic,
  woocommerceMatchResolvedEpic,
  writeSpectrePosLinkEpic,
  checkSpectrePosLinkEpic,
  recheckSpectrePosLinkOnSettingsEpic,
  woocommerceCheckOrderExistsEpic,
  woocommerceGetProductIdEpic,
  woocommerceDeleteOrderEpic,
  woocommerceRemoveFromOrderEpic,
  woocommerceCheckSKUExistsEpic,
  woocommerceAddToOrderFromSKUEpic,
  woocommerceRemoveFromOrderBySKUEpic,
  woocommerceReplaceOrderEpic,
  woocommerceCreateProductEpic,
  cancelPluginSubscriptionEpic,
  provisionSpectrePOSEpic,
  pollProvisionStatusEpic,
  getSpectreposSiteStatusEpic,
  woocommerceCheckBallInOpenOrderEpic,
  hbsShopEpics,
);
